import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { cancelProductTag, isErpApiSuccess } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { createOldMetalIdempotencyKey } from "../utils/oldMetalPurchaseErpAdapter";
import { numPool } from "../utils/productGroupPool";

function CancelTagModal({
  show,
  onHide,
  product,
  buttonClass,
  onSuccess,
}) {
  const [nwt, setNwt] = useState("");
  const [pcs, setPcs] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!show || !product) return;
    const productNwt = numPool(product.nwt ?? product.nWt);
    const productPcs = numPool(product.pcs ?? product.current_pcs, 1);
    setNwt(productNwt > 0 ? String(productNwt) : "");
    setPcs(productPcs > 0 ? String(productPcs) : "1");
    setReason("");
    setDate(new Date().toISOString().split("T")[0]);
    setConfirmed(false);
  }, [show, product]);

  const handleClose = () => {
    if (!saving) onHide();
  };

  const handleSubmit = async () => {
    if (!product) return;

    const cancelNwt = parseFloat(nwt);
    const cancelPcs = parseInt(pcs, 10);
    const maxNwt = numPool(product.nwt ?? product.nWt);
    const maxPcs = numPool(product.pcs ?? product.current_pcs, 1);

    if (!Number.isFinite(cancelNwt) || cancelNwt <= 0) {
      toast.error("Enter net weight greater than 0");
      return;
    }
    if (!Number.isFinite(cancelPcs) || cancelPcs <= 0) {
      toast.error("Enter pieces greater than 0");
      return;
    }
    if (cancelNwt > maxNwt) {
      toast.error(`Cannot cancel more than product nwt (${maxNwt} g)`);
      return;
    }
    if (cancelPcs > maxPcs) {
      toast.error(`Cannot cancel more than product pcs (${maxPcs})`);
      return;
    }

    if (!confirmed) {
      setConfirmed(true);
      return;
    }

    const branch_id = getSessionBranchId();
    if (branch_id == null) {
      toast.error("Branch not set in session");
      return;
    }

    const idempotencyKey = createOldMetalIdempotencyKey();
    const payload = {
      type: "cancel_tag",
      branch_id,
      product_id: product.id ? Number(product.id) : undefined,
      tag_no: product.tagNo || product.tag_no || undefined,
      product_group_id:
        product.productGroupId || product.product_group_id
          ? Number(product.productGroupId || product.product_group_id)
          : undefined,
      nwt: cancelNwt,
      pcs: cancelPcs,
      idempotency_key: idempotencyKey,
      reason: reason.trim() || undefined,
      date,
    };

    setSaving(true);
    try {
      const res = await cancelProductTag(payload, { idempotencyKey });
      if (isErpApiSuccess(res)) {
        toast.success("Tag cancelled — weight returned to untagged pool");
        onSuccess?.(res);
        onHide();
      } else {
        toast.error(res?.message || "Failed to cancel tag");
        setConfirmed(false);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to cancel tag");
      setConfirmed(false);
    } finally {
      setSaving(false);
    }
  };

  if (!product) return null;

  const displayGwt = numPool(product.gWt ?? product.gwt);
  const displayNwt = numPool(product.nwt ?? product.nWt);
  const displayPcs = numPool(product.pcs ?? product.current_pcs, 1);
  const status = String(product.status || "").toUpperCase();

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Cancel Tag / Break to Pool</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 small">
          <div>
            <strong>Tag:</strong> {product.tagNo || product.tag_no || "—"}
          </div>
          <div>
            <strong>Product group:</strong>{" "}
            {product.productGroup || product.product_group_name || "—"}
          </div>
          <div>
            <strong>Status:</strong> {status || "—"}
          </div>
          <div className="text-muted mt-1">
            Current stock — G.Wt: {displayGwt.toFixed(3)} g | N.Wt:{" "}
            {displayNwt.toFixed(3)} g | Pcs: {displayPcs}
          </div>
        </div>

        {confirmed ? (
          <div className="alert alert-warning py-2 small mb-3">
            This will remove tag weight and add it to the untagged pool.
            Continue?
          </div>
        ) : null}

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">N.Wt to cancel (g)</Form.Label>
          <Form.Control
            type="number"
            step="0.001"
            min="0"
            className={buttonClass}
            value={nwt}
            onChange={(e) => setNwt(e.target.value)}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Pcs</Form.Label>
          <Form.Control
            type="number"
            min="1"
            className={buttonClass}
            value={pcs}
            onChange={(e) => setPcs(e.target.value)}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Date</Form.Label>
          <Form.Control
            type="date"
            className={buttonClass}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Label className="small fw-semibold">Reason / remarks</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            className={buttonClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional"
            disabled={saving}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          className={buttonClass}
          onClick={handleClose}
          disabled={saving}
        >
          Close
        </Button>
        <Button
          variant={confirmed ? "danger" : "primary"}
          className={buttonClass}
          onClick={handleSubmit}
          disabled={saving || status !== "ACTIVE"}
        >
          {saving
            ? "Saving…"
            : confirmed
              ? "Confirm cancel tag"
              : "Cancel tag"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CancelTagModal;
